<template>
  <v-card elevation="4" class="d-flex v-card--mwidth-600px">
    <v-card-title class="d-flex justify-center">
      <h2 class="font-weight-800">
        {{ $t("heading.loginPage.emailVerification.title") }}
      </h2>
    </v-card-title>
    <v-card-text class="pb-8">
      <template v-if="verificationErrors.length">
        <div
          class="login-message login-message--error mb-6"
          v-for="(error, index) in verificationErrors"
          :key="index"
        >
          <div class="login-message__icon-box">
            <v-icon size="24" color="error">$alertwarning</v-icon>
          </div>
          <div class="login-message__text-box">
            <h6 class="p-3 login-message__title pb-0">
              {{ error }}
            </h6>
          </div>
        </div>
      </template>
      <div class="text--heading text-center p-4 mb-8">
        {{ $t("heading.loginPage.emailVerification.info1") }}
        <b>{{ email }}</b>
        {{ $t("heading.loginPage.emailVerification.info2") }}
      </div>
      <v-form class="form" @submit.prevent>
        <div class="four-digit-input">
          <v-text-field
            v-for="(digit, index) in verificationCodeSplit"
            :key="index"
            :class="verificationErrors.length ? 'error--text':''"
            outlined
            v-model="verificationCodeSplit[index]"
            large
            hide-details="auto"
            validate-on-blur
            maxlength="1"
            @input="handleCodeInput(index)"
            @keydown.delete="handleCodeDelete(index)"
            @keydown.enter.prevent
            @keypress="allowOnlyNumbers"
            ref="inputs"
          >
          </v-text-field>
        </div>
      </v-form>
      <v-btn
        large
        @click.prevent="verifyCode()"
        :color="isCodeComplete ? 'primary' : ''"
        block
        elevation="0"
        :disabled="!isCodeComplete"
        :outlined="!isCodeComplete"
        :loading="verifying"
      >
        <span class="font-weight-600">{{ $t("heading.loginPage.emailVerification.confirmEmailAddress") }}</span>
        <template v-slot:loader>
          <loader :color="'white'" :size="24" />
        </template>
      </v-btn>

      <v-btn
        v-if="!codeSentWait"
        class="mt-4"
        @click="resendCode()"
        color="primary"
        block
        large
        outlined
        elevation="0"
        :loading="verifying"
      >
        <span class="font-weight-bold">
          {{ $t("button.resentCode") }}
        </span>
      </v-btn>
      <v-btn
        v-else
        class="mt-4"
        block
        large
        outlined
        elevation="0"
        disabled
        style="background-color: transparent !important"
        :loading="verifying"
      >
        <span class="font-weight-bold" style="color: var(--v-gray-lighten2)">
          {{ $t("heading.loginPage.emailVerification.resendCodeMessage", {seconds: secondsLeft}) }}
        </span>
      </v-btn>
      <div class="p-4 mt-4 text-center">
        {{ $t("heading.loginPage.emailVerification.info3") }}
      </div>
    </v-card-text>
    <div class="login-bottom-box p-d text--base">
      {{ $t("heading.loginPage.emailVerification.questions.title") }}
      <a :href="supportUrl" target="_blank" class="ml-1 mr-1">
        {{ $t("heading.loginPage.emailVerification.questions.link") }}
      </a>
    </div>
  </v-card>
</template>
<script>
import Api from "@/apis/Api";
import Auth from "@/apis/Auth";
import Loader from "../components/Loader.vue";

export default {
  props: {
    email: String,
    codeSent: Boolean,
    registering: {
      type: Boolean,
      default: true,
    },
    skipDashboard: {
      type: Boolean,
      default: false,
    },
  },
  components: { Loader },
  data() {
    return {
      codeSentWait: false,
      verificationErrors: [],
      verificationCode: "",
      verifying: false,
      verificationCodeSplit: ["", "", "", ""],
      timer: null,
      secondsLeft: 60,
    };
  },
  computed: {
    isCodeComplete() {
      return this.verificationCodeSplit.every((digit) => digit !== "");
    },
    supportUrl() {
      return this.$store.state.home?.app?.support_link;
    },
  },
  mounted: function () {
    document.addEventListener("keyup", this.handleEnter);
    if (this.codeSent) {
      this.startCodeSentPeriod();
    }
  },
  destroyed: function () {
    document.removeEventListener("keyup", this.handleEnter);
  },
  methods: {
    allowOnlyNumbers(event) {
      if (!/^\d$/.test(event.key)) {
        event.preventDefault();
      }
    },
    handleCodeInput(index) {
      this.verificationCodeSplit[index] = this.verificationCodeSplit[index]
        .replace(/\D/g, "")
        .slice(0, 1);
      if (this.verificationCodeSplit[index] && index < 3) {
        this.$refs.inputs[index + 1].focus();
      }
      this.verificationCode = this.verificationCodeSplit.join("");
    },
    handleCodeDelete(index) {
      if (!this.verificationCodeSplit[index] && index > 0) {
        this.$refs.inputs[index - 1].focus();
      }
      this.verificationCode = this.verificationCodeSplit.join("");
    },
    handleEnter(e) {
      if (e.code === "Enter" || e.code === "NumpadEnter") {
        this.verifyCode();
      }
    },
    startCodeSentPeriod() {
      this.secondsLeft = 60;
      this.codeSentWait = true;
      this.timer = setInterval(() => {
        if (this.secondsLeft > 0) {
          this.secondsLeft--;
        } else {
          clearInterval(this.timer);
          this.codeSentWait = false;
        }
      }, 1000);
    },
    beforeUnmount() {
      clearInterval(this.timer);
    },
    verifyCode() {
      this.verifying = true;
      Auth.verifyCode(this.verificationCode)
        .then(() => {
          this.$root.$emit("loggedIn", true);
          Auth.hideRecaptchaBadge();
          if (!this.skipDashboard) {
            this.$router.push({ path: this.$route.query.redirect || "/" });
          }
          if (this.registering) {
            this.$root.$emit("after-login");
          }
        })
        .catch((error) => {
          this.verificationErrors = Api.getErrorMessages(error);
        })
        .finally(() => {
          this.verifying = false;
        });
    },
    resendCode() {
      this.verifying = true;
      Api.post("/resend-code")
        .catch((error) => {
          this.verificationErrors = Api.getErrorMessages(error);
        })
        .finally(() => {
          this.verifying = false;
          this.startCodeSentPeriod();
        });
    },
  },
};
</script>

<style src="./Login.scss" lang="scss" scoped />